import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  usersService,
  type UserFilters,
  type UserCreateInput,
  type UserUpdateInput,
  type UserRoleAssignment,
  type UserAccessAssignment,
  type PaginatedUsersResult,
} from '../api';
import type { User } from '@lognai/core-platform';
import { UserStatus } from '../types/domain-enums';
import { queryKeys } from './query-keys';

/**
 * Hook to fetch users list. Server-paginated: returns the full envelope so
 * the page can render `total`/`totalPages` without re-counting. Pass
 * `filters.page` / `filters.limit` to advance pages.
 */
export function useUsers(
  filters?: UserFilters,
  options?: Omit<UseQueryOptions<PaginatedUsersResult>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.users.list(filters || {}),
    queryFn: () => usersService.list(filters),
    ...options,
  });
}

/**
 * Hook to fetch a single user
 */
export function useUser(
  userId: string | undefined,
  options?: Omit<UseQueryOptions<User>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: queryKeys.users.detail(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getById(userId);
    },
    enabled: !!userId,
    ...options,
  });
}

/**
 * Hook to fetch user's roles
 */
export function useUserRoles(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.roles(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getRoles(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user's access assignments
 */
export function useUserAccessAssignments(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.accessAssignments(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getAccessAssignments(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user's effective permissions
 */
export function useUserPermissions(userId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.users.permissions(userId || ''),
    queryFn: () => {
      if (!userId) throw new Error('User ID is required');
      return usersService.getEffectivePermissions(userId);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to create a user
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreateInput) => usersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to update a user
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdateInput }) =>
      usersService.update(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.delete(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to update user status
 */
export function useUpdateUserStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, status }: { userId: string; status: UserStatus }) =>
      usersService.updateStatus(userId, status),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.lists() });
    },
  });
}

/**
 * Hook to assign role to user
 */
export function useAssignUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, assignment }: { userId: string; assignment: UserRoleAssignment }) =>
      usersService.assignRole(userId, assignment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.roles(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to remove role from user
 */
export function useRemoveUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, roleId }: { userId: string; roleId: string }) =>
      usersService.removeRole(userId, roleId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.roles(variables.userId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to assign direct access to user
 */
export function useAssignUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, assignment }: { userId: string; assignment: UserAccessAssignment }) =>
      usersService.assignAccess(userId, assignment),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.accessAssignments(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to remove direct access from user
 */
export function useRemoveUserAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, accessId }: { userId: string; accessId: string }) =>
      usersService.removeAccess(userId, accessId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.accessAssignments(variables.userId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.users.permissions(variables.userId) });
    },
  });
}

/**
 * Hook to reset user password
 */
export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      usersService.resetPassword(userId, newPassword),
  });
}

/**
 * Hook to enable MFA for user
 */
export function useEnableUserMfa() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.enableMfa(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
    },
  });
}

/**
 * Hook to disable MFA for user
 */
export function useDisableUserMfa() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => usersService.disableMfa(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
    },
  });
}
